// Moving "look" effects drawn with the captions (preview + on-device export share this code).
// fx = { type, intensity (0..1), color }. Deterministic in t so exports match the preview.

export const FX_TYPES = [
  ["none", "None"],
  ["golden", "Golden glow — particles & light streaks"],
  ["dreamy", "Dreamy — soft focus & floating dust"],
  ["retro", "Retro film — grain, warm tone, flicker"],
  ["audio", "Audio visual — spectrum bars & pulsing rings"],
  ["metal", "Metallic sheen — moving highlight"],
  ["dreamscape", "Dreamscape — mist, clouds & petals"],
  ["knockout", "Video inside letters"],
  ["cinematic", "Cinematic — letterbox, flare & light sweep"],
];
const FULL_FRAME = new Set(["golden", "dreamy", "retro", "audio", "dreamscape", "cinematic"]);
export const fxOf = (o, config) => {
  const fx = (o && o.fx) || (config && config.fx);
  return fx && fx.type && fx.type !== "none" ? fx : null;
};
export const isKnockout = (fx) => fx && fx.type === "knockout";

const rnd = (i, s = 0) => { const x = Math.sin(i * 127.1 + s * 311.7) * 43758.5453; return x - Math.floor(x); };
const hexA = (hex, a) => {
  const h = String(hex || "#ffffff").replace("#", "");
  const v = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h.slice(0, 6), 16);
  const n = Number.isNaN(v) ? 0xffffff : v; // "#000000" parses to 0 — must stay black, not fall back to white
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${Math.max(0, Math.min(1, a))})`;
};
const k = (fx) => Math.max(0, Math.min(1, fx.intensity ?? 0.7));

function particles(ctx, t, W, H, n, color, rise, sizeMul, alphaMul, drift = 0.02) {
  for (let i = 0; i < n; i++) {
    const sp = 0.03 + rnd(i, 1) * 0.06;
    const y = (1 - ((rnd(i, 2) + t * sp * rise) % 1)) * H;
    const x = (rnd(i, 3) + Math.sin(t * 0.6 + i) * drift) * W;
    const r = (1 + rnd(i, 4) * 3) * sizeMul * (W / 1080);
    const tw = 0.5 + 0.5 * Math.sin(t * 3 + i * 1.7);
    ctx.globalAlpha = alphaMul * (0.35 + 0.65 * tw);
    const g = ctx.createRadialGradient(x, y, 0, x, y, r * 3);
    g.addColorStop(0, color); g.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, r * 3, 0, Math.PI * 2); ctx.fill();
  }
  ctx.globalAlpha = 1;
}

function vignette(ctx, W, H, color, a) {
  const g = ctx.createRadialGradient(W / 2, H / 2, Math.min(W, H) * 0.35, W / 2, H / 2, Math.max(W, H) * 0.75);
  g.addColorStop(0, "rgba(0,0,0,0)"); g.addColorStop(1, hexA(color, a));
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
}

// Full-frame layer behind the captions.
export function drawFxBack(ctx, fx, t, W, H, energy = 0, beat = null) {
  const a = k(fx); ctx.save();
  if (fx.type === "golden") vignette(ctx, W, H, "#2a1600", 0.55 * a);
  if (fx.type === "dreamy") {
    vignette(ctx, W, H, "#ffffff", 0.18 * a);
    ctx.globalAlpha = 0.12 * a; ctx.fillStyle = hexA(fx.color || "#ffe9f5", 1); ctx.fillRect(0, 0, W, H);
  }
  if (fx.type === "retro") {
    ctx.globalCompositeOperation = "multiply";
    ctx.fillStyle = hexA(fx.color || "#d9a05b", 0.35 * a); ctx.fillRect(0, 0, W, H);
    ctx.globalCompositeOperation = "source-over";
    vignette(ctx, W, H, "#1a0d00", 0.6 * a);
  }
  if (fx.type === "dreamscape") {
    for (let i = 0; i < 5; i++) { // drifting mist / cloud bands
      const y = H * (0.15 + i * 0.18) + Math.sin(t * 0.25 + i) * H * 0.02;
      const x = ((rnd(i, 7) + t * 0.012 * (1 + i * 0.3)) % 1.4 - 0.2) * W;
      const g = ctx.createRadialGradient(x, y, 0, x, y, W * 0.45);
      g.addColorStop(0, hexA(fx.color || "#ffffff", 0.16 * a)); g.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    }
  }
  if (fx.type === "audio") {
    const bars = 48, bw = W / bars, base = H * 0.98, col = fx.color || "#00F0FF";
    for (let i = 0; i < bars; i++) {
      // Bass bars (left) kick on the beat, highs (right) follow overall loudness.
      const band = i / bars, lvl = beat ? (band < 0.35 ? Math.max(beat.pulse, beat.level * 0.5) : beat.level * (0.6 + 0.4 * rnd(i, Math.floor(t * 20)))) : energy;
      const h = H * (0.03 + 0.18 * a * (0.2 + 0.8 * lvl) * (0.5 + 0.5 * Math.abs(Math.sin(t * (2 + rnd(i, 5) * 5) + i))));
      ctx.fillStyle = hexA(col, 0.25 + 0.5 * a);
      ctx.fillRect(i * bw + bw * 0.15, base - h, bw * 0.7, h);
    }
    ctx.strokeStyle = hexA(col, 0.6 * a); ctx.lineWidth = Math.max(1, W / 540); ctx.beginPath();
    for (let x = 0; x <= W; x += W / 120) {
      const y = H * 0.12 + Math.sin(x / W * 18 + t * 6) * H * 0.02 * (0.3 + energy) * Math.sin(x / W * Math.PI);
      if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  ctx.restore();
}

// Full-frame layer above the captions.
export function drawFxFront(ctx, fx, t, W, H, energy = 0) {
  const a = k(fx), e = 0.65 + 0.7 * energy; ctx.save(); // particles swell & brighten on each beat
  if (fx.type === "golden") { ctx.globalCompositeOperation = "lighter"; particles(ctx, t, W, H, 46, fx.color || "#FFC94D", 1, 1.4 * e, 0.8 * a * e); }
  if (fx.type === "dreamy") particles(ctx, t, W, H, 36, "rgba(255,255,255,0.9)", 0.35, 1.1 * e, 0.55 * a * e, 0.05);
  if (fx.type === "dreamscape") { // falling petals
    for (let i = 0; i < 26; i++) {
      const sp = 0.04 + rnd(i, 8) * 0.05;
      const y = ((rnd(i, 9) + t * sp) % 1.1 - 0.05) * H;
      const x = (rnd(i, 10) + Math.sin(t * 0.8 + i) * 0.04) * W;
      const s = (6 + rnd(i, 11) * 10) * (W / 1080);
      ctx.save(); ctx.translate(x, y); ctx.rotate(t * (0.5 + rnd(i, 12)) + i);
      ctx.globalAlpha = 0.75 * a; ctx.fillStyle = i % 3 ? "#FFB7D5" : "#FFE4EF";
      ctx.beginPath(); ctx.ellipse(0, 0, s, s * 0.55, 0, 0, Math.PI * 2); ctx.fill(); ctx.restore();
    }
  }
  if (fx.type === "retro") {
    const n = Math.round(900 * a);
    for (let i = 0; i < n; i++) {
      const fr = Math.floor(t * 24);
      ctx.fillStyle = rnd(i, fr) > 0.5 ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.14)";
      ctx.fillRect(rnd(i, fr + 0.3) * W, rnd(i, fr + 0.7) * H, W / 540, W / 540);
    }
    const fr = Math.floor(t * 12);
    if (rnd(fr, 2) > 0.7) { ctx.fillStyle = "rgba(255,255,255,0.18)"; ctx.fillRect(rnd(fr, 3) * W, 0, Math.max(1, W / 900), H); }
    ctx.fillStyle = `rgba(0,0,0,${(0.05 + 0.08 * rnd(Math.floor(t * 18), 4)) * a})`; ctx.fillRect(0, 0, W, H); // flicker
  }
  if (fx.type === "cinematic") {
    const bar = H * 0.1 * (0.6 + 0.4 * a);
    ctx.fillStyle = "#000"; ctx.fillRect(0, 0, W, bar); ctx.fillRect(0, H - bar, W, bar);
  }
  ctx.restore();
}

// Per-caption accents around/over the text block (bbox in canvas px).
export function drawFxText(ctx, fx, t, W, H, box, start, lit, beat = null) {
  const a = k(fx), age = t - start;
  ctx.save();
  if (fx.type === "golden" || fx.type === "metal" || fx.type === "cinematic") {
    const p = ((age * (fx.type === "metal" ? 0.6 : 0.8)) % 1.6) - 0.3; // light streak sweeping across the words
    const x = box.x + p * box.w;
    const g = ctx.createLinearGradient(x - box.h, 0, x + box.h, 0);
    const c = fx.type === "golden" ? "255,226,140" : "255,255,255";
    g.addColorStop(0, `rgba(${c},0)`); g.addColorStop(0.5, `rgba(${c},${0.55 * a})`); g.addColorStop(1, `rgba(${c},0)`);
    ctx.globalCompositeOperation = "lighter"; ctx.fillStyle = g;
    ctx.beginPath(); ctx.moveTo(x - box.h * 0.6, box.y + box.h); ctx.lineTo(x, box.y); ctx.lineTo(x + box.h * 0.6, box.y); ctx.lineTo(x, box.y + box.h); ctx.closePath(); ctx.fill();
  }
  if (fx.type === "cinematic") { // anamorphic flare through the caption line
    const cy = box.y + box.h / 2, g = ctx.createLinearGradient(0, 0, W, 0);
    g.addColorStop(0, "rgba(120,180,255,0)"); g.addColorStop(0.5, `rgba(150,200,255,${0.35 * a})`); g.addColorStop(1, "rgba(120,180,255,0)");
    ctx.globalCompositeOperation = "lighter"; ctx.fillStyle = g; ctx.fillRect(0, cy - Math.max(1, H / 600), W, Math.max(2, H / 300));
  }
  if (fx.type === "audio") { // pulsing rings from the caption centre on each new word
    const cx = box.x + box.w / 2, cy = box.y + box.h / 2;
    for (let r = 0; r < 3; r++) {
      // With a beat map each beat launches a ring; otherwise rings loop steadily.
      const p = beat ? Math.min(1, beat.phase * 1.6 + r * 0.22) : ((age * 1.2 + r / 3) % 1);
      if (beat && p >= 1) continue;
      ctx.strokeStyle = hexA(fx.color || "#00F0FF", (1 - p) * 0.6 * a * (lit ? 1 : 0.6));
      ctx.lineWidth = Math.max(1, W / 400);
      ctx.beginPath(); ctx.ellipse(cx, cy, box.w * (0.55 + p * 0.5), box.h * (0.8 + p * 1.2), 0, 0, Math.PI * 2); ctx.stroke();
    }
  }
  ctx.restore();
}

// Gentle camera drift for dreamy looks (applied to the caption transform).
export function fxDrift(fx, t, W) {
  if (!fx || (fx.type !== "dreamy" && fx.type !== "dreamscape")) return null;
  const a = k(fx);
  return { dx: Math.sin(t * 0.4) * W * 0.01 * a, dy: Math.cos(t * 0.3) * W * 0.008 * a, scale: 1 + 0.025 * a * Math.sin(t * 0.35) };
}

// Video inside letters: darken the whole frame except the glyphs (drawn by drawText on an offscreen canvas).
export function drawKnockout(ctx, fx, W, H, drawText) {
  const off = (typeof OffscreenCanvas !== "undefined") ? new OffscreenCanvas(W, H) : Object.assign(document.createElement("canvas"), { width: W, height: H });
  const o = off.getContext("2d");
  o.fillStyle = hexA(fx.color || "#000000", 0.55 + 0.4 * k(fx)); o.fillRect(0, 0, W, H);
  o.globalCompositeOperation = "destination-out";
  drawText(o);
  ctx.drawImage(off, 0, 0);
}

export const fxCollect = (objects, config) => {
  const out = new Map();
  objects.forEach((o) => { const fx = fxOf(o, config); if (fx && FULL_FRAME.has(fx.type) && !out.has(fx.type)) out.set(fx.type, fx); });
  if (!objects.length && config && config.fx && FULL_FRAME.has(config.fx.type)) out.set(config.fx.type, config.fx);
  return [...out.values()];
};
